import { useState, useMemo } from 'react';
import { usePermissions } from 'AuthorizePermission';
import { PermissionActionTypes } from 'helper/type/action.type';
import { useGetAllBranches } from 'hooks/useGetAllBranches';
import { useGetMe } from 'hooks/useGetMe';
import useCommon from 'stores/useCommon';

interface UseSelectMultiBranchesReturn {
  selectedSourceBranches: number[] | undefined;
  setSelectedSourceBranches: (branches: number[] | undefined) => void;
  appliedSourceBranches: number[] | undefined;
  setAppliedSourceBranches: (branches: number[] | undefined) => void;
  hasSourceBranchesPermission: boolean;
  hasAppliedSourceBranches: boolean;
  setHasAppliedSourceBranches: (value: boolean) => void;
  allBranches: any[] | undefined;
  selectedBranchesData: any[];
  getBranchIdsString: () => string | undefined;
}

export const useSelectMultiBranches = (
  permission: PermissionActionTypes
): UseSelectMultiBranchesReturn => {
  // Options parameter kept for API consistency and future extensibility
  const { currentBranch } = useCommon();
  const { hasPermission } = usePermissions();
  const { data: branches } = useGetAllBranches();
  const { data: me } = useGetMe();

  const [selectedSourceBranches, setSelectedSourceBranches] = useState<number[] | undefined>(
    undefined
  );
  const [appliedSourceBranches, setAppliedSourceBranches] = useState<number[] | undefined>(
    undefined
  );

  const hasSourceBranchesPermission = hasPermission(permission);

  const [hasAppliedSourceBranches, setHasAppliedSourceBranches] = useState<boolean>(
    !hasSourceBranchesPermission
  );

  const allBranches = useMemo(() => {
    return branches?.filter(branch => me?.branch_ids.includes(branch.id));
  }, [branches, me?.branch_ids]);

  const selectedBranchesData = useMemo(() => {
    if (!hasSourceBranchesPermission || !allBranches || !appliedSourceBranches?.length) {
      return [];
    }

    return allBranches.filter(branch => appliedSourceBranches.includes(branch.id));
  }, [hasSourceBranchesPermission, allBranches, appliedSourceBranches]);

  const getBranchIdsString = (): string | undefined => {
    if (!hasSourceBranchesPermission) {
      return undefined;
    }
    return (
      appliedSourceBranches?.join(',') || (currentBranch?.id ? String(currentBranch.id) : undefined)
    );
  };

  return {
    selectedSourceBranches,
    setSelectedSourceBranches,
    appliedSourceBranches,
    setAppliedSourceBranches,
    hasSourceBranchesPermission,
    hasAppliedSourceBranches,
    setHasAppliedSourceBranches,
    allBranches,
    selectedBranchesData,
    getBranchIdsString
  };
};
